import { BlockScrollStrategy, ViewportRuler } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { MAT_DIALOG_SCROLL_STRATEGY } from '@angular/material/dialog';

/**
 * BlockScrollStrategy pins <html> at the scroll offset captured on enable().
 * On iOS the on-screen keyboard scrolls the document, so that offset can point
 * past the end of the page — which leaves a gap at the bottom once the keyboard
 * collapses. Reset such an offset before the base strategy records it.
 */
export class KeyboardAwareBlockScrollStrategy extends BlockScrollStrategy {
  override enable(): void {
    if (this.shouldResetScroll()) {
      window.scrollTo(0, 0);
    }

    super.enable();
  }

  private shouldResetScroll(): boolean {
    if (window.scrollY === 0) return false;

    const viewport = window.visualViewport;
    const keyboardIsOpen =
      !!viewport && window.innerHeight - viewport.height > 4;

    const maxScroll = Math.max(
      document.documentElement.scrollHeight - window.innerHeight,
      0
    );

    // Either the keyboard is still up, or the page is scrolled further than its
    // own content allows — both mean the offset came from the keyboard.
    return keyboardIsOpen || window.scrollY > maxScroll;
  }
}

/** Replaces the default block scroll strategy for every CDK overlay that uses one. */
export function provideKeyboardAwareScrollStrategy(): EnvironmentProviders {
  const useFactory = (viewportRuler: ViewportRuler, document: Document) => () =>
    new KeyboardAwareBlockScrollStrategy(viewportRuler, document);

  const deps = [ViewportRuler, DOCUMENT];

  return makeEnvironmentProviders([
    { provide: MAT_DIALOG_SCROLL_STRATEGY, useFactory, deps },
  ]);
}
